import { create } from "zustand";
import { apiFetch } from "@/lib/api";

type VoiceGender = "male" | "female";
type VoiceReplyTrigger = "voice_only" | "always" | "on_request";

interface CategoryVoice {
  gender: VoiceGender;
  tone: string;
}

interface ApiVoiceSettings {
  voiceName: string;
  speed: number;
  language: string;
  autoVoiceReply: boolean;
  updatedAt: string | null;
}

export interface VoiceSettings {
  voiceRepliesEnabled: boolean;
  defaultVoiceGender: VoiceGender;
  voiceReplyTrigger: VoiceReplyTrigger;
  languageAutoSwitch: boolean;
  categoryVoices: Record<string, CategoryVoice>;
}

interface VoiceStore {
  settings: VoiceSettings;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetchSettings: () => Promise<void>;
  updateSettings: (updates: Partial<VoiceSettings>) => Promise<void>;
  toggleVoiceReplies: () => Promise<void>;
  clearError: () => void;
}

const DEFAULT_SETTINGS: VoiceSettings = {
  voiceRepliesEnabled: false,
  defaultVoiceGender: "female",
  voiceReplyTrigger: "voice_only",
  languageAutoSwitch: true,
  categoryVoices: {
    family: { gender: "female", tone: "warm" },
    friend: { gender: "female", tone: "casual" },
    business: { gender: "male", tone: "professional" },
    vip: { gender: "female", tone: "professional" },
    other: { gender: "female", tone: "neutral" },
  },
};

function mergeApiSettings(
  apiSettings: ApiVoiceSettings,
  current: VoiceSettings = DEFAULT_SETTINGS,
): VoiceSettings {
  return {
    ...current,
    voiceRepliesEnabled: apiSettings.autoVoiceReply,
    defaultVoiceGender: apiSettings.voiceName === "Abebe" ? "male" : "female",
  };
}

function toApiUpdate(
  updates: Partial<VoiceSettings>,
): Partial<Pick<ApiVoiceSettings, "voiceName" | "autoVoiceReply">> {
  const apiUpdate: Partial<Pick<ApiVoiceSettings, "voiceName" | "autoVoiceReply">> = {};
  if (updates.voiceRepliesEnabled !== undefined) {
    apiUpdate.autoVoiceReply = updates.voiceRepliesEnabled;
  }
  if (updates.defaultVoiceGender !== undefined) {
    apiUpdate.voiceName = updates.defaultVoiceGender === "male" ? "Abebe" : "Hanna";
  }
  return apiUpdate;
}

export const useVoiceStore = create<VoiceStore>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  isLoading: false,
  isSaving: false,
  error: null,

  fetchSettings: async () => {
    set({ isLoading: true, error: null });

    try {
      const { settings } = await apiFetch<{ settings: ApiVoiceSettings }>(
        "/api/voice",
      );
      set({ settings: mergeApiSettings(settings, get().settings), isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load voice settings";
      set({ error: message, isLoading: false });
    }
  },

  updateSettings: async (updates) => {
    const previousSettings = get().settings;
    const merged = { ...previousSettings, ...updates };
    const apiUpdate = toApiUpdate(updates);

    set({ isSaving: true, error: null, settings: merged });

    if (Object.keys(apiUpdate).length === 0) {
      set({ isSaving: false });
      return;
    }

    try {
      const { settings: saved } = await apiFetch<{ settings: ApiVoiceSettings }>("/api/voice", {
        method: "PUT",
        body: apiUpdate,
      });
      set({ settings: mergeApiSettings(saved, merged), isSaving: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save voice settings";
      set({ error: message, settings: previousSettings, isSaving: false });
    }
  },

  toggleVoiceReplies: async () => {
    const current = get().settings;
    await get().updateSettings({ voiceRepliesEnabled: !current.voiceRepliesEnabled });
  },

  clearError: () => set({ error: null }),
}));

export type { VoiceGender, VoiceReplyTrigger, CategoryVoice };
